import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  alpha,
  Tooltip,
  Chip,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from "@mui/material";
import { CheckCircle, Block, CalendarMonth, EventAvailable, MeetingRoom } from "@mui/icons-material";
import axios from "axios";
import config from "../../config";
import { useRoomNames } from './useRoomNames';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

export default function RoomCalendar() {
  const theme = useTheme();
  const { roomLabel } = useRoomNames();
  const navigate = useNavigate();

  // Room Action modal — opens when an AVAILABLE cell is clicked.
  // { room, date, fromDate, toDate }  (dates are 'YYYY-MM-DD' strings)
  const [roomAction, setRoomAction] = useState(null);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [data, setData] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const hotelId = sessionStorage.getItem("companyId");

  // #8: default to TODAY → next 7 days (not the whole month) and load it
  // automatically. Dashboard + sidebar calendar both use this component, so
  // both now show the same compact 7-day window.
  useEffect(() => {
    const now = new Date();
    const first = new Date(now);
    const last = new Date(now);
    last.setDate(last.getDate() + 7);
    const fmt = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`;
    const fs = fmt(first);
    const fe = fmt(last);
    setStartDate(fs);
    setEndDate(fe);
    fetchCalendar(fs, fe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCalendar = async (s = startDate, e = endDate) => {
    if (!s || !e) {
      toast.error("Please select both start and end dates.");
      return;
    }

    if (new Date(s) > new Date(e)) {
      toast.error("Start date must be before or equal to end date.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = sessionStorage.getItem("token");

      const res = await axios.get(
        `${config.API_BASE_URL}/home/GetRoomcelenderquery/${hotelId}/${s}/${e}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const result = res.data || [];

      setData(result);

      const roomList = [...new Set(result.map((x) => x.roomNumber))].sort(
        (a, b) => a - b
      );
      setRooms(roomList);

      const dateList = [...new Set(result.map((x) => x.date.substring(0, 10)))].sort();
      setDates(dateList);
    } catch (err) {
      console.error(err);
      // Never keep stale calendar data after a failed search.
      setData([]);
      setRooms([]);
      setDates([]);
      setError(
        err?.response?.data?.message ||
          "Failed to load calendar data. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const getRecord = (room, date) =>
    data.find(
      (x) => x.roomNumber === room && x.date.substring(0, 10) === date
    );

  const fmtDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt)) return "";
    return dt.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Tooltip content for a cell: guest detail when booked, else "Available".
  // Force white text on the dark tooltip bg so the content is actually readable
  // (the theme tooltip is slate900; without an explicit color the Typography
  // inherited dark text -> invisible). Labels + clear date range, mobile-safe.
  const cellTooltip = (record) => {
    if (!record?.isBooked) return "Available";
    const rowLabel = { fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.7)", lineHeight: 1.5 };
    const rowValue = { fontSize: 12.5, fontWeight: 600, color: "#fff", lineHeight: 1.5 };
    return (
      <Box sx={{ p: 0.75, minWidth: 168, color: "#fff" }}>
        <Typography sx={{ fontWeight: 800, fontSize: 14, color: "#fff", mb: 0.5 }}>
          {record.guestName?.trim() || "Guest"}
        </Typography>
        {record.mobile && (
          <Typography sx={{ ...rowValue, mb: 0.25 }}>📞 {record.mobile}</Typography>
        )}
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5 }}>
          <Typography sx={rowLabel}>Check-In</Typography>
          <Typography sx={rowValue}>{fmtDate(record.checkIn)}</Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5 }}>
          <Typography sx={rowLabel}>Check-Out</Typography>
          <Typography sx={rowValue}>{fmtDate(record.checkOut)}</Typography>
        </Box>
        {record.status && (
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5, mt: 0.25 }}>
            <Typography sx={rowLabel}>Status</Typography>
            <Typography sx={rowValue}>{record.status}</Typography>
          </Box>
        )}
      </Box>
    );
  };

  const isToday = (dateStr) => {
    const today = new Date().toISOString().split("T")[0];
    return dateStr === today;
  };

  // 'YYYY-MM-DD' + n days → 'YYYY-MM-DD'
  const addDaysStr = (dateStr, n) => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + n);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
  };

  // Click on an AVAILABLE cell → open the Room Action modal with the clicked
  // date as From and the next day as To (both editable in the modal).
  const openRoomAction = (room, date, roomType) => {
    setRoomAction({
      room,
      date,
      roomType: roomType || "",
      fromDate: date,
      toDate: addDaysStr(date, 1),
    });
  };

  // Hold Room → HoldRoom page with the room + dates pre-filled (read there
  // from location.state.prefill).
  const goHoldRoom = () => {
    if (!roomAction) return;
    navigate("/admin/HoldRoom", {
      state: {
        prefill: {
          hotelId,
          roomNumber: roomAction.room,
          fromDate: roomAction.fromDate,
          toDate: roomAction.toDate,
        },
      },
    });
  };

  // Book Room → Manage Inventory booking wizard with hotel + dates + room
  // pre-filled (read there from location.state.prefill).
  const goBookRoom = () => {
    if (!roomAction) return;
    navigate("/admin/manageinventory", {
      state: {
        prefill: {
          hotelId,
          roomNumber: roomAction.room,
          roomType: roomAction.roomType,
          checkInDate: roomAction.fromDate,
          checkOutDate: roomAction.toDate,
        },
      },
    });
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f0f4ff 0%, #e6f0ff 100%)",
        bgcolor: "grey.50",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 5 },
          borderRadius: 4,
          backdropFilter: "blur(12px)",
          background: alpha(theme.palette.background.paper, 0.92),
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          boxShadow: "0 20px 60px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.04)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 4,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <CalendarMonth color="primary" sx={{ fontSize: 36 }} />
            <Typography
              variant="h4"
              fontWeight={700}
              color="text.primary"
              sx={{ letterSpacing: "-0.5px" }}
            >
              Room Availability Calendar
            </Typography>
          </Box>

          <Chip
            label="Real-time status"
            color="primary"
            variant="outlined"
            size="small"
          />
        </Box>

        {/* Filters */}
        <Grid container spacing={3} alignItems="flex-end" mb={5}>
          <Grid item xs={12} sm={5} md={4}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              sx={{ bgcolor: "background.paper" }}
            />
          </Grid>

          <Grid item xs={12} sm={5} md={4}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              sx={{ bgcolor: "background.paper" }}
            />
          </Grid>

          <Grid item xs={12} sm={2} md={4}>
            <Button
              variant="contained"
              disableElevation
              onClick={() => fetchCalendar()}
              disabled={loading}
              fullWidth
              sx={{
                height: 56,
                borderRadius: 3,
                fontWeight: 600,
                textTransform: "none",
                fontSize: "1.05rem",
                background: "linear-gradient(90deg, #3f51b5 0%, #5c6bc0 100%)",
                "&:hover": {
                  background: "linear-gradient(90deg, #303f9f 0%, #3f51b5 100%)",
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Load Calendar"}
            </Button>
          </Grid>
        </Grid>

        {error && (
          <Typography color="error" align="center" mb={3} fontWeight={500}>
            {error}
          </Typography>
        )}

        {/* Calendar */}
        {loading ? (
          <Box sx={{ textAlign: "center", py: 10 }}>
            <CircularProgress size={60} thickness={4} />
            <Typography mt={3} color="text.secondary">
              Loading availability...
            </Typography>
          </Box>
        ) : rooms.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 10, color: "text.secondary" }}>
            <Typography variant="h6" gutterBottom>
              No data available
            </Typography>
            <Typography>
              Select date range and load calendar to view room status.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto", borderRadius: 3, boxShadow: 1 }}>
            <Table
              sx={{
                minWidth: 900,
                borderCollapse: "separate",
                borderSpacing: "0 8px",
                "& .MuiTableCell-root": {
                  border: 0,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      position: "sticky",
                      left: 0,
                      background: alpha(theme.palette.background.paper, 0.98),
                      zIndex: 3,
                      minWidth: 140,
                      pl: 3,
                      py: 2,
                      borderRight: `1px solid ${theme.palette.divider}`,
                      boxShadow: "2px 0 8px rgba(0,0,0,0.04)",
                    }}
                  >
                    Room
                  </TableCell>

                  {dates.map((d) => {
                    const dateObj = new Date(d);
                    const dayName = dateObj.toLocaleDateString("en-US", {
                      weekday: "short",
                    });
                    const dayNum = dateObj.getDate();
                    const isCurrentDay = isToday(d);

                    return (
                      <TableCell
                        key={d}
                        align="center"
                        sx={{
                          minWidth: 80,
                          py: 2,
                          background: isCurrentDay
                            ? alpha(theme.palette.primary.main, 0.12)
                            : "transparent",
                          borderBottom: `2px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          fontWeight={500}
                        >
                          {dayName}
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          color={isCurrentDay ? "primary" : "text.primary"}
                        >
                          {dayNum}
                        </Typography>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>

              <TableBody>
                {rooms.map((room) => (
                  <TableRow
                    key={room}
                    sx={{
                      "&:hover": { bgcolor: alpha(theme.palette.action.hover, 0.5) },
                      transition: "background 0.2s",
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        position: "sticky",
                        left: 0,
                        background: theme.palette.background.paper,
                        zIndex: 2,
                        pl: 3,
                        py: 2,
                        borderRight: `1px solid ${theme.palette.divider}`,
                        boxShadow: "2px 0 12px rgba(0,0,0,0.05)",
                      }}
                    >
                      Room {roomLabel(room)}
                    </TableCell>

                    {dates.map((date) => {
                      const record = getRecord(room, date);
                      const booked = record?.isBooked ?? false;

                      return (
                        <TableCell key={date} align="center">
                          <Tooltip
                            title={cellTooltip(record)}
                            arrow
                            placement="top"
                          >
                            <Box
                              onClick={() => {
                                if (!booked) openRoomAction(room, date, record?.roomType);
                              }}
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: "12px",
                                margin: "0 auto",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: booked
                                  ? "linear-gradient(135deg, #ef5350 0%, #e57373 100%)"
                                  : "linear-gradient(135deg, #66bb6a 0%, #81c784 100%)",
                                color: "white",
                                boxShadow: booked
                                  ? "0 4px 14px rgba(239,83,80,0.4)"
                                  : "0 4px 14px rgba(102,187,106,0.4)",
                                transition: "all 0.22s ease",
                                cursor: "pointer",
                                "&:hover": {
                                  transform: "scale(1.12) translateY(-2px)",
                                  boxShadow: booked
                                    ? "0 8px 24px rgba(239,83,80,0.5)"
                                    : "0 8px 24px rgba(102,187,106,0.5)",
                                },
                              }}
                            >
                              {booked ? (
                                <Block fontSize="medium" />
                              ) : (
                                <CheckCircle fontSize="medium" />
                              )}
                            </Box>
                          </Tooltip>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* Legend */}
        {rooms.length > 0 && (
          <Box
            sx={{
              mt: 4,
              display: "flex",
              gap: 4,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "6px",
                  background: "linear-gradient(135deg, #66bb6a, #81c784)",
                }}
              />
              <Typography variant="body2" color="text.secondary">
                Available
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "6px",
                  background: "linear-gradient(135deg, #ef5350, #e57373)",
                }}
              />
              <Typography variant="body2" color="text.secondary">
                Booked
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  background: alpha(theme.palette.primary.main, 0.16),
                }}
              />
              <Typography variant="body2" color="text.secondary">
                Today
              </Typography>
            </Box>
          </Box>
        )}
        {/* Room Action modal — opens on clicking an AVAILABLE cell */}
        <Dialog
          open={!!roomAction}
          onClose={() => setRoomAction(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Room Action</DialogTitle>
          <DialogContent dividers>
            {roomAction && (
              <Stack spacing={2} sx={{ mt: 0.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography color="text.secondary">Room Number</Typography>
                  <Typography fontWeight={700}>
                    Room {roomLabel(roomAction.room)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography color="text.secondary">Selected Date</Typography>
                  <Typography fontWeight={600}>{fmtDate(roomAction.date)}</Typography>
                </Box>
                <TextField
                  type="date"
                  label="From Date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={roomAction.fromDate}
                  onChange={(e) =>
                    setRoomAction((p) => ({ ...p, fromDate: e.target.value }))
                  }
                />
                <TextField
                  type="date"
                  label="To Date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={roomAction.toDate}
                  onChange={(e) =>
                    setRoomAction((p) => ({ ...p, toDate: e.target.value }))
                  }
                />
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1, flexWrap: "wrap" }}>
            <Button onClick={() => setRoomAction(null)} color="inherit">
              Cancel
            </Button>
            <Button onClick={goHoldRoom} variant="outlined" startIcon={<MeetingRoom />}>
              Hold Room
            </Button>
            <Button onClick={goBookRoom} variant="contained" startIcon={<EventAvailable />}>
              Book Room
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
}